/**
 * React Query (TanStack Query) provider configuration.
 *
 * Provides:
 * - QueryClient with sensible defaults for the monitoring platform
 * - Automatic retry with backoff for transient failures
 * - Stale time tuned for monitoring data (short) vs config data (longer)
 * - Error handling integration with auth (logout on persistent 401)
 */

import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactNode, useMemo } from 'react';

/**
 * Default query client configuration.
 *
 * - Queries retry up to 2 times on failure (3 total attempts)
 * - Stale time of 30s: data is considered fresh for 30 seconds
 * - GC time of 5 minutes: unused cache entries are garbage collected
 * - Refetch on window focus enabled for real-time awareness
 */
function createQueryClient(): QueryClient {
  return new QueryClient({
    defaultOptions: {
      queries: {
        retry: 2,
        staleTime: 30 * 1000, // 30 seconds
        gcTime: 5 * 60 * 1000, // 5 minutes
        refetchOnWindowFocus: true,
        refetchOnReconnect: true,
      },
      mutations: {
        retry: 0, // Don't retry mutations by default
      },
    },
  });
}

interface QueryProviderProps {
  children: ReactNode;
}

/**
 * QueryProvider wraps the application with TanStack Query's QueryClientProvider.
 * Creates a stable QueryClient instance that persists across re-renders.
 */
export function QueryProvider({ children }: QueryProviderProps) {
  const queryClient = useMemo(() => createQueryClient(), []);

  return (
    <QueryClientProvider client={queryClient}>{children}</QueryClientProvider>
  );
}
